import { toJpeg, toPng } from 'html-to-image';

import { fixCaptureDomIds, promoteSvgImageHrefs } from './fixCaptureDomIds';
import type { PreviewCaptureFormat, PreviewCaptureResult } from './previewCaptureRegistry';

export async function rasterizePreviewElement(
  element: HTMLElement,
  options: { format: PreviewCaptureFormat; quality?: number; signal?: AbortSignal },
): Promise<PreviewCaptureResult> {
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  if (width <= 0 || height <= 0) {
    throw new Error('The preview frame has no visible size.');
  }

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await document.fonts.ready;
  }

  promoteSvgImageHrefs(element);
  fixCaptureDomIds(element);

  const rasterOptions = {
    cacheBust: true,
    // html-to-image defaults this to devicePixelRatio. width/height are CSS
    // pixels, so a 2x display would return a bitmap that does not match them.
    pixelRatio: 1,
    fetchRequestInit: options.signal ? { signal: options.signal } : undefined,
    // The capture host sits off-screen. Force the clone visible so inherited
    // visibility does not rasterize as an empty JPEG/PNG.
    style: { visibility: 'visible', opacity: '1' },
  };
  const dataUrl =
    options.format === 'png'
      ? await toPng(element, rasterOptions)
      : await toJpeg(element, { ...rasterOptions, quality: options.quality ?? 0.85 });

  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  if (!base64) {
    throw new Error('Preview capture returned an empty image.');
  }

  return {
    base64,
    mimeType: options.format === 'png' ? 'image/png' : 'image/jpeg',
    width,
    height,
  };
}
